/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

export type SelectionMode = 'single' | 'multiple' | 'multiple-replace';

export type SelectionEdge = 'top' | 'middle' | 'bottom';

/** Modifier keys resolved from a pointer or keyboard event. */
export type SelectionIntent = {
  /** Shift: extend from the anchor to the target. */
  isRange: boolean;
  /**
   * Cmd on Apple platforms, Ctrl elsewhere. Ignored in `multiple`, where an
   * unmodified click already toggles.
   */
  isToggle: boolean;
};

/**
 * Where a range is measured from.
 *
 * `baseSelectedIds` is the selection as it stood immediately after the anchor
 * was placed. A `multiple-replace` range unions its visible members with the
 * range so it can be grown and shrunk without losing earlier picks.
 * `isSelected` is the state the anchor row was placed into, which a `multiple`
 * range paints across its members.
 */
export type SelectionAnchor = {
  baseSelectedIds: ReadonlySet<string>;
  id: string;
  isSelected: boolean;
} | null;

type ComputeNextSelectionInput = {
  anchor: SelectionAnchor;
  intent: SelectionIntent;
  /** Ids of every selectable, non-disabled item, visible or not. */
  selectableIds: ReadonlySet<string>;
  /** Currently selected ids, including items inside collapsed groups. */
  selectedIds: ReadonlySet<string>;
  selectionMode: SelectionMode;
  targetId: string;
  /** Ids of the items the user can currently see, in visual order. */
  visibleIds: readonly string[];
  /** Maps an item id to the id of the item that owns its group. */
  parentIdById: ReadonlyMap<string, string>;
};

type ComputeNextSelectionResult = {
  anchor: SelectionAnchor;
  selectedIds: Set<string>;
};

/**
 * Ids that are selected but cannot be acted on, because they are disabled or
 * not selectable. A replacing selection leaves them exactly as they are rather
 * than clearing them behind the user's back.
 */
function getLockedIds(
  selectedIds: ReadonlySet<string>,
  selectableIds: ReadonlySet<string>,
): string[] {
  return [...selectedIds].filter((id) => !selectableIds.has(id));
}

function getRangeIds(
  visibleIds: readonly string[],
  fromId: string,
  toId: string,
  selectableIds: ReadonlySet<string>,
): string[] {
  const fromIndex = visibleIds.indexOf(fromId);
  const toIndex = visibleIds.indexOf(toId);
  const [start, end] =
    fromIndex <= toIndex ? [fromIndex, toIndex] : [toIndex, fromIndex];
  return visibleIds.slice(start, end + 1).filter((id) => selectableIds.has(id));
}

/**
 * The anchor may have been scrolled out of reach by a collapse. Walking up to
 * its nearest visible ancestor keeps a range measurable from roughly where the
 * user put it, instead of silently collapsing the range to a single item.
 */
function resolveVisibleAnchorId(
  anchor: SelectionAnchor,
  visibleIds: readonly string[],
  parentIdById: ReadonlyMap<string, string>,
): string | null {
  if (anchor === null) {
    return null;
  }
  const visible = new Set(visibleIds);
  let candidate: string | undefined = anchor.id;
  while (candidate !== undefined) {
    if (visible.has(candidate)) {
      return candidate;
    }
    candidate = parentIdById.get(candidate);
  }
  return null;
}

function toggleId(
  selectedIds: ReadonlySet<string>,
  targetId: string,
): Set<string> {
  const next = new Set(selectedIds);
  if (next.has(targetId)) {
    next.delete(targetId);
  } else {
    next.add(targetId);
  }
  return next;
}

function anchorAt(
  id: string,
  selectedIds: ReadonlySet<string>,
): SelectionAnchor {
  return {
    baseSelectedIds: new Set(selectedIds),
    id,
    isSelected: selectedIds.has(id),
  };
}

/**
 * Resolves a selection gesture into the complete next selection. Pure: the
 * caller supplies the current state and applies the result.
 */
export function computeNextSelection({
  anchor,
  intent,
  parentIdById,
  selectableIds,
  selectedIds,
  selectionMode,
  targetId,
  visibleIds,
}: ComputeNextSelectionInput): ComputeNextSelectionResult {
  if (!selectableIds.has(targetId) || !visibleIds.includes(targetId)) {
    return { anchor, selectedIds: new Set(selectedIds) };
  }

  if (selectionMode === 'single') {
    const next = new Set([targetId]);
    return { anchor: anchorAt(targetId, next), selectedIds: next };
  }

  const anchorId = intent.isRange
    ? resolveVisibleAnchorId(anchor, visibleIds, parentIdById)
    : null;

  if (selectionMode === 'multiple') {
    if (!intent.isRange || anchorId === null) {
      const next = toggleId(selectedIds, targetId);
      return { anchor: anchorAt(targetId, next), selectedIds: next };
    }
    // A range repeats whatever the anchor row was last put into. An anchor
    // resolved from a collapsed group may not be selectable at all, in which
    // case there is no prior action to repeat and selecting is the useful default.
    const isPaintingSelected = selectableIds.has(anchorId)
      ? (anchor?.isSelected ?? true)
      : true;
    const next = new Set(selectedIds);
    getRangeIds(visibleIds, anchorId, targetId, selectableIds).forEach((id) => {
      if (isPaintingSelected) {
        next.add(id);
      } else {
        next.delete(id);
      }
    });
    return { anchor, selectedIds: next };
  }

  const lockedIds = getLockedIds(selectedIds, selectableIds);

  if (intent.isRange) {
    const rangeIds = getRangeIds(
      visibleIds,
      anchorId ?? targetId,
      targetId,
      selectableIds,
    );
    const baseIds =
      anchor === null
        ? []
        : [...anchor.baseSelectedIds].filter(
            (id) => selectableIds.has(id) && visibleIds.includes(id),
          );
    const next = new Set([...lockedIds, ...baseIds, ...rangeIds]);
    return {
      anchor: anchorId === null ? anchorAt(targetId, next) : anchor,
      selectedIds: next,
    };
  }

  if (intent.isToggle) {
    const next = toggleId(selectedIds, targetId);
    return { anchor: anchorAt(targetId, next), selectedIds: next };
  }

  const next = new Set([...lockedIds, targetId]);
  return { anchor: anchorAt(targetId, next), selectedIds: next };
}

/**
 * Marks each selected item's position within its run of adjacent selected
 * items, so a run can be styled as one block.
 */
export function computeSelectionEdges(
  visibleIds: readonly string[],
  selectedIds: ReadonlySet<string>,
): Map<string, SelectionEdge> {
  const edges = new Map<string, SelectionEdge>();
  visibleIds.forEach((id, index) => {
    if (!selectedIds.has(id)) {
      return;
    }
    const hasPreviousSelected =
      index > 0 && selectedIds.has(visibleIds[index - 1]);
    const hasNextSelected =
      index < visibleIds.length - 1 && selectedIds.has(visibleIds[index + 1]);
    if (hasPreviousSelected && hasNextSelected) {
      edges.set(id, 'middle');
    } else if (hasNextSelected) {
      edges.set(id, 'top');
    } else if (hasPreviousSelected) {
      edges.set(id, 'bottom');
    }
  });
  return edges;
}

export function countChangedIds(
  previous: ReadonlySet<string>,
  next: ReadonlySet<string>,
): number {
  let changed = 0;
  previous.forEach((id) => {
    if (!next.has(id)) {
      changed++;
    }
  });
  next.forEach((id) => {
    if (!previous.has(id)) {
      changed++;
    }
  });
  return changed;
}

export function formatSelectionAnnouncement(selectedCount: number): string {
  if (selectedCount === 0) {
    return 'No items selected';
  }
  if (selectedCount === 1) {
    return '1 item selected';
  }
  return `${selectedCount} items selected`;
}
